import { useContext, useEffect, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchMetricMetadata, orgId } from "../utils/dashboardUtils";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { indicatorsEnv } from "../utils/environment";

interface LegendMetricModalProps {
    id: number;
    isOpen: boolean;
    geoDetails: GeoDetails[]
    onClose: any;
    svg: string;
    geoTag: GeoTag | null;
    selectionActive: boolean;
};

const LegendMetricModal = ({ id, isOpen, onClose, geoDetails, svg, geoTag, selectionActive }: LegendMetricModalProps) => {
    const [showMetadata, setShowMetadata] = useState(isOpen);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const { metricCategories } = useContext(MetricCategoriesContext);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicators`;

    const fetchIndicatorData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
    
                // Combine and categorize the results
                const combinedData = results.flatMap(result => result.data);
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchIndicatorData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    useEffect(() => {
        setShowMetadata(isOpen);
        if (isOpen && id) {
            fetchAllData();
        }
    }, [isOpen, id]); // Fetch metadata when modal opens or ID changes

    const fetchAllData = async () => {
        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }

        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchDistributionData(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to fetch data: ${errorMessage}`));
        } finally {
            setLoading(false);
        }

        /* *** Bypass queries for state and national data for now
        // Calculate geoPrefix if state data has not been fetched
        const geoPrefix = stateData ? null : calculateGeoPrefix(geoDetails);
        // Fetch state data if not already fetched
        if (!stateData && geoPrefix) {
            await fetchData(fetchStateData, setStateData, stateData, id, geoPrefix);
        } 
        // Fetch national data if not already fetched
        if (!nationalData) {
            await fetchData(fetchNationalData, setNationalData, nationalData, id);
        }
        */
    };

    const handleClose = () => {
        setShowMetadata(false);
        onClose();
    };

    return (
        <MetricMetadataModal
            isOpen={showMetadata}
            onClose={handleClose}
            id={id}
            disaggData={disaggregatedData}
            metadata={metadata}
            stateData={null}
            nationalData={null}
            distributionData={distributionData}
            countyDistributionData={countyDistributionData}
            selectionActive={selectionActive}
            geoTag={geoTag}
            svg={svg}
            loading={loading}
            error={error} // Pass error state if needed
        />
    );
};


export default LegendMetricModal;
